import { useState, useEffect } from "react";
import { ethers } from "ethers";

export interface DeploymentInfo {
  network: string;
  chainId: number;
  deployer: string;
  contracts: {
    testERC20: string;
    openCollection: string;
    closedCollection: string;
    directSaleOpenETH: string;
    directSaleClosedERC20: string;
    auctionSaleOpenETH: string;
    auctionSaleClosedERC20: string;
  };
  testAccounts: string[];
  deployedCollections: Array<{
    address: string;
    type: "open" | "closed";
    name: string;
  }>;
  deployedSales: Array<{
    address: string;
    type: "direct" | "auction";
    collectionAddress: string;
  }>;
}

export const useDeploymentInfo = () => {
  const [deploymentInfo, setDeploymentInfo] = useState<DeploymentInfo | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDeploymentInfo = async () => {
      try {
        // Load factory addresses first to get network configuration
        let factoryData: any = null;
        try {
          const factoryResponse = await fetch("/factory-addresses.json");
          if (factoryResponse.ok) {
            factoryData = await factoryResponse.json();
          }
        } catch (factoryError) {
          console.log("Could not load factory addresses:", factoryError);
        }

        // Use injected wallet provider if available, otherwise fallback to Base public RPC
        let provider;
        if (window.ethereum) {
          provider = new ethers.BrowserProvider(window.ethereum);
        } else {
          // Use Base public RPC as fallback
          const rpcUrl = "https://mainnet.base.org";
          provider = new ethers.JsonRpcProvider(rpcUrl);
        }

        // Get network information
        const network = await provider.getNetwork();
        const chainId = Number(network.chainId);

        // Use connected wallet address as deployer
        let deployer;
        if (window.ethereum) {
          // Get the connected wallet address
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts.length > 0) {
            deployer = accounts[0];
          } else {
            throw new Error('No wallet connected. Please connect your wallet to load collections.');
          }
        } else {
          // Fallback to factory deployer if no wallet
          deployer = factoryData?.deployer || "0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266";
        }

        // Create test accounts (standard Anvil accounts)
        const testAccounts = [
          "0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266",
          "0x70997970C51812dc3A010C7d01b50e0d17dc79C8",
          "0x3C44CdDdB6a900fa2b585dd299e03d12FA4293BC",
          "0x90F79bf6EB2c4f870365E785982E1f101E93b906",
          "0x15d34AAf54267DB7D7c367839AAf71A00a2C6A65",
        ];

        // Load factory addresses to query deployed contracts
        let contracts = {
          testERC20: "",
          proofOfCuration: "",
          openCollection: "",
          closedCollection: "",
          directSaleOpenETH: "",
          directSaleClosedERC20: "",
          auctionSaleOpenETH: "",
          auctionSaleClosedERC20: "",
        };

        let deployedCollections: Array<{
          address: string;
          type: "open" | "closed";
          name: string;
        }> = [];
        let deployedSales: Array<{
          address: string;
          type: "direct" | "auction";
          collectionAddress: string;
        }> = [];

        try {
          // Use the already loaded factory data
          if (factoryData) {
            // Factory ABIs for querying deployed contracts and collection names
            const openCollectionFactoryABI = [
              "function getDeployedContracts(address _owner) external view returns (address[])",
            ];
            const closedCollectionFactoryABI = [
              "function getDeployedContracts(address _owner) external view returns (address[])",
            ];
            const directSaleFactoryABI = [
              "function getDeployedContracts(address _owner) external view returns (address[])",
            ];
            const auctionSaleFactoryABI = [
              "function getDeployedContracts(address _owner) external view returns (address[])",
            ];

            // Collection ABI to get name
            const collectionABI = [
              "function name() external view returns (string)",
              "function symbol() external view returns (string)",
              "function totalSupply() external view returns (uint256)",
              "function maxSupply() external view returns (uint256)",
            ];

            // Store factory addresses for reference
            if (factoryData.factories.fakeERC20) {
              contracts.testERC20 = factoryData.factories.fakeERC20;
            }
            if (factoryData.factories.proofOfCuration) {
              contracts.proofOfCuration = factoryData.factories.proofOfCuration;
            }

            // Query deployed collections from each factory
            if (factoryData.factories.openCollectionFactory) {
              const openFactory = new ethers.Contract(
                factoryData.factories.openCollectionFactory,
                openCollectionFactoryABI,
                provider
              );
              const openCollections = await openFactory.getDeployedContracts(
                deployer
              );

              console.log(
                `Found ${openCollections.length} open collections for deployer ${deployer}`
              );

              // Get names for each open collection
              for (const address of openCollections) {
                try {
                  console.log(
                    `Trying to get name for open collection: ${address}`
                  );
                  const collection = new ethers.Contract(
                    address,
                    collectionABI,
                    provider
                  );
                  const name = await collection.name();
                  console.log(`Open collection ${address} -> name: ${name}`);
                  deployedCollections.push({ address, type: "open", name });
                } catch (err) {
                  console.log(
                    `Could not get name for collection ${address}:`,
                    err
                  );
                  deployedCollections.push({
                    address,
                    type: "open",
                    name: `Collection ${address.slice(0, 8)}...`,
                  });
                }
              }
            }

            if (factoryData.factories.closedCollectionFactory) {
              const closedFactory = new ethers.Contract(
                factoryData.factories.closedCollectionFactory,
                closedCollectionFactoryABI,
                provider
              );
              const closedCollections =
                await closedFactory.getDeployedContracts(deployer);

              console.log(
                `Found ${closedCollections.length} closed collections for deployer ${deployer}`
              );

              // Get names for each closed collection
              for (const address of closedCollections) {
                try {
                  const collection = new ethers.Contract(
                    address,
                    collectionABI,
                    provider
                  );
                  const name = await collection.name();
                  deployedCollections.push({ address, type: "closed", name });
                } catch (err) {
                  console.log(
                    `Could not get name for collection ${address}:`,
                    err
                  );
                  deployedCollections.push({
                    address,
                    type: "closed",
                    name: `Collection ${address.slice(0, 8)}...`,
                  });
                }
              }
            }

            // Sale ABI to get collection address
            const saleABI = [
              "function collectionContract() external view returns (address)",
            ];

            // Query deployed sales from each factory
            if (factoryData.factories.directSaleFactory) {
              const directFactory = new ethers.Contract(
                factoryData.factories.directSaleFactory,
                directSaleFactoryABI,
                provider
              );
              const directSales = await directFactory.getDeployedContracts(
                deployer
              );

              for (const address of directSales) {
                try {
                  const sale = new ethers.Contract(address, saleABI, provider);
                  const collectionAddress = await sale.collectionContract();
                  console.log(
                    `Direct sale ${address} -> collection ${collectionAddress}`
                  );
                  deployedSales.push({
                    address,
                    type: "direct",
                    collectionAddress,
                  });
                } catch (err) {
                  console.log(
                    `Could not get collection address for sale ${address}:`,
                    err
                  );
                  deployedSales.push({
                    address,
                    type: "direct",
                    collectionAddress: "",
                  });
                }
              }
            }

            if (factoryData.factories.auctionSaleFactory) {
              const auctionFactory = new ethers.Contract(
                factoryData.factories.auctionSaleFactory,
                auctionSaleFactoryABI,
                provider
              );
              const auctionSales = await auctionFactory.getDeployedContracts(
                deployer
              );

              for (const address of auctionSales) {
                try {
                  const sale = new ethers.Contract(address, saleABI, provider);
                  const collectionAddress = await sale.collectionContract();
                  console.log(
                    `Auction sale ${address} -> collection ${collectionAddress}`
                  );
                  deployedSales.push({
                    address,
                    type: "auction",
                    collectionAddress,
                  });
                } catch (err) {
                  console.log(
                    `Could not get collection address for sale ${address}:`,
                    err
                  );
                  deployedSales.push({
                    address,
                    type: "auction",
                    collectionAddress: "",
                  });
                }
              }
            }
          }
        } catch (factoryError) {
          console.log("Could not load factory contracts:", factoryError);
          // Continue with empty contracts if factory loading fails
        }

        const data: DeploymentInfo = {
          network: factoryData?.network || "anvil",
          chainId,
          deployer,
          contracts,
          testAccounts,
          deployedCollections,
          deployedSales,
        };

        setDeploymentInfo(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    loadDeploymentInfo();
  }, []);

  return { deploymentInfo, loading, error };
};
